import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Package, Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { supplierAPI } from '../../services/api';
import { Inventory, SolarPanel } from '../../types';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { PanelSearch } from '../common/PanelSearch';

export const SupplierInventory: React.FC = () => {
  const [inventory, setInventory] = useState<Inventory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Inventory | null>(null);
  const [selectedPanel, setSelectedPanel] = useState<SolarPanel | null>(null);

  const [newItem, setNewItem] = useState({
    panelId: '',
    quantity: 0,
    pricePerUnit: 0
  });

  useEffect(() => {
    loadInventory();
  }, []);

  const loadInventory = async () => {
    try {
      const data = await supplierAPI.getInventory();
      setInventory(data);
    } catch (err: any) {
      setError('Failed to load inventory');
    }
  };

  const handleAddInventory = async () => {
    setLoading(true);
    try {
      await supplierAPI.addInventory(newItem);
      setSuccess('Inventory added successfully');
      setShowAddModal(false);
      setNewItem({ panelId: '', quantity: 0, pricePerUnit: 0 });
      setSelectedPanel(null);
      loadInventory();
    } catch (err: any) {
      setError('Failed to add inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInventory = async (id: string, data: any) => {
    setLoading(true);
    try {
      await supplierAPI.updateInventory(id, data);
      setSuccess('Inventory updated successfully');
      setEditingItem(null);
      loadInventory();
    } catch (err: any) {
      setError('Failed to update inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInventory = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this inventory item?')) return;
    
    setLoading(true);
    try {
      await supplierAPI.deleteInventory(id);
      setSuccess('Inventory deleted successfully');
      loadInventory();
    } catch (err: any) {
      setError('Failed to delete inventory');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">My Inventory</h1>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Inventory
          </Button>
        </div>
      </motion.div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            <Package className="h-5 w-5 text-[#63B56F] mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">Inventory Items</h2>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Panel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Price/Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inventory.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {item.panel?.SKU}
                      </div>
                      <div className="text-sm text-gray-500">
                        {item.panel?.Description}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <Input
                        type="number"
                        value={editingItem.quantity}
                        onChange={(e) => setEditingItem({ ...editingItem, quantity: parseInt(e.target.value) })}
                        className="w-20"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">{item.quantity}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingItem?.id === item.id ? (
                      <Input
                        type="number"
                        step="0.01"
                        value={editingItem.pricePerUnit || 0}
                        onChange={(e) => setEditingItem({ ...editingItem, pricePerUnit: parseFloat(e.target.value) })}
                        className="w-24"
                      />
                    ) : (
                      <span className="text-sm text-gray-900">
                        {Number(item?.pricePerUnit).toFixed(2) || 'N/A'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {editingItem?.id === item.id ? (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateInventory(item.id, {
                            quantity: editingItem.quantity,
                            pricePerUnit: editingItem.pricePerUnit
                          })}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setEditingItem(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setEditingItem(item)}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleDeleteInventory(item.id)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add Inventory Item
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Solar Panel
                </label>
                <PanelSearch
                  selectedPanel={selectedPanel}
                  onSelect={(panel) => {
                    setSelectedPanel(panel);
                    setNewItem({ ...newItem, panelId: panel?.id || '' });
                  }}
                  placeholder="Search by brand, name, or SKU..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <Input
                  type="number"
                  value={newItem.quantity}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Price per Unit ($)
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={newItem.pricePerUnit}
                  onChange={(e) => setNewItem({ ...newItem, pricePerUnit: parseFloat(e.target.value) })}
                />
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedPanel(null);
                  setNewItem({ panelId: '', quantity: 0, pricePerUnit: 0 });
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddInventory}
                loading={loading}
                disabled={!newItem.panelId || !newItem.quantity}
              >
                Add Item
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
